import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Phone, Mail, Download, CheckCircle2, Circle, Archive, ChevronLeft, ChevronRight, Star, X, Copy } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { btnDanger, btnSecondary, PanelHeader } from "./_shared";
import ConfirmDialog from "@/components/site/ConfirmDialog";

const STATUS_STYLES = {
  new: { label: "New", cls: "text-[#FF6B00] border-[#FF6B00]", icon: Circle },
  contacted: { label: "Contacted", cls: "text-blue-400 border-blue-400/60", icon: CheckCircle2 },
  completed: { label: "Completed", cls: "text-green-400 border-green-400/60", icon: CheckCircle2 },
  archived: { label: "Archived", cls: "text-white/40 border-white/20", icon: Archive },
};

const PAGE_SIZE = 25;

export default function EstimatesPanel() {
  const { adminApi } = useAuth();
  const { content } = useContent();
  const reviewUrl = content?.contact?.review_url || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reviewModal, setReviewModal] = useState(null); // {name, email, review_url, email_delivered}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (filter !== "all") params.status_filter = filter;
      const { data } = await adminApi.get("/estimates", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [adminApi, filter, skip]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset paging when filter changes
  useEffect(() => {
    setSkip(0);
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/estimates/${id}`, { status });
      setItems((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await adminApi.delete(`/estimates/${pendingDelete.id}`);
      toast.success("Deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const requestReview = async (lead) => {
    try {
      const { data } = await adminApi.post(`/estimates/${lead.id}/request-review`);
      // Update the row so the "Review Requested" badge appears immediately
      setItems((s) => s.map((x) => (x.id === lead.id ? { ...x, review_requested_at: new Date().toISOString(), review_email_sent: data.email_delivered } : x)));
      if (data.email_delivered) {
        toast.success(`Review request emailed to ${data.customer_name || lead.name}`);
      } else {
        // Fallback: open modal with copy-to-clipboard message
        setReviewModal({
          name: data.customer_name || lead.name,
          email: data.customer_email || lead.email,
          review_url: data.review_url,
          email_delivered: false,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCSV = async () => {
    try {
      const params = filter !== "all" ? { status_filter: filter } : {};
      const res = await adminApi.get("/estimates/export", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimates-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pageStart = total === 0 ? 0 : skip + 1;
  const pageEnd = Math.min(skip + PAGE_SIZE, total);
  const hasPrev = skip > 0;
  const hasNext = skip + PAGE_SIZE < total;

  return (
    <div data-testid="estimates-panel">
      <PanelHeader
        eyebrow="00"
        title="Estimate Leads"
        description="Every request from the website comes here. Call or email the customer, then mark it contacted."
        actions={
          <button data-testid="estimates-export" onClick={exportCSV} disabled={!total} className={btnSecondary}>
            <Download size={14} /> Export CSV
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "new", "contacted", "completed", "archived"].map((k) => (
          <button
            key={k}
            data-testid={`estimates-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 text-xs tracking-widest uppercase font-display border transition-colors ${
              filter === k ? "bg-[#FF6B00] border-[#FF6B00] text-white" : "bg-transparent border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 bg-[#1A1D21] p-10 text-center text-white/50" data-testid="estimates-empty">
          No leads in this filter.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((e) => {
            const status = e.status || "new";
            const S = STATUS_STYLES[status] || STATUS_STYLES.new;
            return (
              <div
                key={e.id}
                data-testid={`estimate-row-${e.id}`}
                className="border border-white/10 bg-[#1A1D21] p-5"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-display uppercase text-xl tracking-wide">{e.name}</div>
                      <span className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1 border ${S.cls}`}>
                        <S.icon size={10} /> {S.label}
                      </span>
                      <span className="text-[10px] tracking-widest uppercase text-white/50 border border-white/15 px-2 py-1">
                        {e.project_type}
                      </span>
                      {e.email_sent && (
                        <span className="text-[10px] tracking-widest uppercase text-green-400/80 border border-green-400/30 px-2 py-1">
                          Email Sent
                        </span>
                      )}
                      {e.review_requested_at && (
                        <span
                          data-testid={`estimate-review-requested-${e.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-yellow-400 border border-yellow-400/40 px-2 py-1"
                          title={`Review request sent ${new Date(e.review_requested_at).toLocaleString()}${e.review_email_sent ? " (email delivered)" : " (link copied — email not configured)"}`}
                        >
                          <Star size={10} className="fill-yellow-400" /> Review Requested
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-white/70">
                      <a href={`tel:${e.phone}`} className="flex items-center gap-2 hover:text-[#FF6B00]">
                        <Phone size={13} /> {e.phone}
                      </a>
                      <a href={`mailto:${e.email}`} className="flex items-center gap-2 hover:text-[#FF6B00] break-all">
                        <Mail size={13} /> {e.email}
                      </a>
                      <span className="text-white/40 text-xs">{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                    {e.message && (
                      <p className="mt-3 text-sm text-white/70 whitespace-pre-wrap border-l-2 border-white/10 pl-4">{e.message}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {status !== "contacted" && status !== "completed" && (
                      <button
                        onClick={() => updateStatus(e.id, "contacted")}
                        data-testid={`estimate-contact-${e.id}`}
                        className="text-xs tracking-widest uppercase font-display border border-blue-400/40 text-blue-400 hover:bg-blue-400 hover:text-black px-3 py-2 transition-colors"
                      >
                        Mark Contacted
                      </button>
                    )}
                    {(status === "contacted" || status === "new") && (
                      <button
                        onClick={() => updateStatus(e.id, "completed")}
                        data-testid={`estimate-complete-${e.id}`}
                        className="text-xs tracking-widest uppercase font-display border border-green-400/40 text-green-400 hover:bg-green-400 hover:text-black px-3 py-2 transition-colors"
                      >
                        Job Complete
                      </button>
                    )}
                    {status === "completed" && !e.review_requested_at && (
                      <button
                        onClick={() => requestReview(e)}
                        data-testid={`estimate-request-review-${e.id}`}
                        disabled={!reviewUrl}
                        title={reviewUrl ? "Send this customer a link to leave a review" : "Add a Review URL under Contact Info to enable this"}
                        className="inline-flex items-center gap-1 text-xs tracking-widest uppercase font-display bg-[#FF6B00] hover:bg-[#E65A00] text-white px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Star size={12} /> Request Review
                      </button>
                    )}
                    {status !== "archived" && (
                      <button
                        onClick={() => updateStatus(e.id, "archived")}
                        className="text-xs tracking-widest uppercase font-display border border-white/20 text-white/60 hover:border-white/50 hover:text-white px-3 py-2 transition-colors"
                      >
                        Archive
                      </button>
                    )}
                    {status !== "new" && (
                      <button
                        onClick={() => updateStatus(e.id, "new")}
                        className="text-xs tracking-widest uppercase font-display border border-white/20 text-white/60 hover:border-white/50 hover:text-white px-3 py-2 transition-colors"
                      >
                        Reopen
                      </button>
                    )}
                    <button onClick={() => setPendingDelete(e)} className={btnDanger}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 0 && (
        <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
          <div className="text-xs text-white/50 tracking-widest uppercase" data-testid="estimates-count">
            {pageStart}–{pageEnd} of {total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={!hasPrev}
              data-testid="estimates-prev"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-display tracking-widest uppercase border border-white/15 text-white/70 hover:text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={12} /> Prev
            </button>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={!hasNext}
              data-testid="estimates-next"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-display tracking-widest uppercase border border-white/15 text-white/70 hover:text-white hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this lead?"
        description={
          pendingDelete
            ? `The estimate request from ${pendingDelete.name} will be permanently deleted.`
            : ""
        }
        confirmLabel="Delete Lead"
        onConfirm={remove}
        testid="estimates-delete-dialog"
      />

      {reviewModal && <ReviewRequestModal data={reviewModal} onClose={() => setReviewModal(null)} />}
    </div>
  );
}

function ReviewRequestModal({ data, onClose }) {
  const firstName = (data.name || "").split(" ")[0] || "there";
  const smsBody = `Hi ${firstName}, thanks for choosing LC Builders for your project! If you have a minute, would you leave us a quick review? ${data.review_url} — Hondo`;
  const mailtoSubject = "Thanks from LC Builders — mind leaving us a quick review?";
  const mailtoBody =
    `Hi ${firstName},\n\n` +
    `Thanks for choosing LC Builders LLC — it was a pleasure working with you.\n\n` +
    `If you have a minute, would you mind leaving us a quick review? It genuinely helps a small business like ours.\n\n` +
    `${data.review_url}\n\n` +
    `Thanks again,\nHondo Castruita\nLC Builders LLC`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="review-request-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl bg-[#1A1D21] border border-white/10 my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-display uppercase tracking-wider text-xl">Send Review Request</div>
            <div className="text-xs text-white/50 mt-1">
              Email not configured — copy the message below and send it via text or your own email.
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs tracking-widest uppercase text-white/50 mb-1">To</div>
            <div className="font-display text-lg">{data.name}</div>
            <div className="text-sm text-white/70">{data.email}</div>
          </div>

          <div>
            <div className="text-xs tracking-widest uppercase text-white/50 mb-2">SMS / text template</div>
            <div className="bg-[#121212] border border-white/10 p-4 text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
              {smsBody}
            </div>
            <button
              type="button"
              onClick={() => copy(smsBody, "SMS message")}
              data-testid="review-copy-sms"
              className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] px-4 py-2 text-xs font-display tracking-widest uppercase transition-colors"
            >
              <Copy size={12} /> Copy SMS
            </button>
          </div>

          <div className="border-t border-white/10 pt-5">
            <div className="text-xs tracking-widest uppercase text-white/50 mb-2">Or open in your email app</div>
            <a
              href={`mailto:${encodeURIComponent(data.email)}?subject=${encodeURIComponent(mailtoSubject)}&body=${encodeURIComponent(mailtoBody)}`}
              data-testid="review-mailto"
              className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E65A00] px-5 py-3 font-display tracking-widest uppercase text-white text-sm"
            >
              <Mail size={14} /> Open Email
            </a>
            <button
              type="button"
              onClick={() => copy(data.review_url, "Review link")}
              className="ml-3 inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] px-4 py-2 text-xs font-display tracking-widest uppercase transition-colors"
            >
              <Copy size={12} /> Copy Link
            </button>
          </div>

          <p className="text-xs text-white/40 pt-2 border-t border-white/10">
            Tip: paste your Resend API key in the backend .env to email review requests automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
